import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSuppliers, forgetSupplierMemory, getSupplierContact, money } from "@/lib/apiClient";
import { SupplierContactEditor } from "@/components/SupplierContactEditor";
import { SupplierInviteDialog } from "@/components/SupplierInviteDialog";
import { StatementMatcher } from "@/components/StatementMatcher";
import { ChasesCard } from "@/components/ChasesCard";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Search, Loader2, AlertTriangle, Copy, Brain, ArrowUpRight, Eraser, X, Mail, CheckCircle2, Clock } from "lucide-react";

const Stat = ({ label, value, className }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={cn("font-mono text-sm font-semibold mt-0.5 break-words", className)}>{value}</div>
  </div>
);

const flagTone = (rate) => {
  if (rate >= 50) return "text-red-500";
  if (rate >= 20) return "text-amber-500";
  return "text-emerald-500";
};

const SupplierCard = ({ s, onOpen, onForget }) => {
  const [contact, setContact] = useState(null);
  const [invite, setInvite] = useState(false);
  useEffect(() => {
    getSupplierContact(s.supplier_key).then(setContact);
  }, [s.supplier_key]);
  return (
  <div
    data-testid={`supplier-card-${s.supplier_key.replace(/[^a-z0-9]+/g, "-")}`}
    className="group min-w-0 overflow-hidden rounded-xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all"
  >
    <button onClick={() => onOpen(s.supplier)} data-testid="supplier-open-btn" className="w-full text-left">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="font-medium truncate">{s.supplier}</div>
            <div className="text-xs text-muted-foreground font-mono">
              {s.invoice_count} invoice{s.invoice_count === 1 ? "" : "s"} · last {s.last_invoice_date || "—"}
            </div>
          </div>
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Spend to date" value={money(s.total_spend)} />
        <Stat label="Approved" value={money(s.approved_spend)} className="text-emerald-500" />
        <Stat
          label="Flag rate"
          value={
            <span className="inline-flex items-center gap-1">
              {s.flagged_count > 0 && <AlertTriangle className="h-3.5 w-3.5" />}
              {s.flag_rate}%
            </span>
          }
          className={flagTone(s.flag_rate)}
        />
        <Stat
          label="Duplicates"
          value={
            <span className="inline-flex items-center gap-1">
              {s.duplicate_count > 0 && <Copy className="h-3.5 w-3.5" />}
              {s.duplicate_count}
            </span>
          }
          className={s.duplicate_count > 0 ? "text-amber-500" : ""}
        />
      </div>
    </button>

    <div className="mt-4 border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
          <Brain className="h-3.5 w-3.5" /> Learned GL codes
        </div>
        {s.learned_codes.length > 0 && (
          <button
            data-testid="forget-all-btn"
            onClick={() => onForget(s, null)}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-orange-400 transition-colors"
          >
            <Eraser className="h-3 w-3" /> Forget all
          </button>
        )}
      </div>
      {s.learned_codes.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Nothing learned yet — approve an invoice to teach D20.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {s.learned_codes.map((c) => (
            <span
              key={c.gl_code}
              data-testid="learned-code-chip"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs"
            >
              <span className="font-mono font-semibold">{c.gl_code}</span>
              <span className="text-muted-foreground truncate max-w-[160px]">{c.gl_name}</span>
              <span className="rounded bg-primary/15 text-primary px-1 font-mono text-[10px]">×{c.count}</span>
              <button
                data-testid={`forget-code-${c.gl_code}`}
                onClick={() => onForget(s, c)}
                className="ml-0.5 text-muted-foreground hover:text-orange-400"
                aria-label={`Forget ${c.gl_code}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
    {contact && <SupplierContactEditor supplierKey={s.supplier_key} contact={contact} onSaved={setContact} />}
    {contact && (
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="supplier-invite-btn" onClick={() => setInvite(true)}>
          <Mail className="h-3.5 w-3.5 mr-1.5" /> {contact.invited_at ? "Resend invoice-address email" : "Ask to send invoices here"}
        </Button>
        {contact.invited_at && (
          <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-500" data-testid="supplier-invited-badge">
            Invited on {new Date(contact.invited_at).toLocaleDateString("en-GB")}
          </span>
        )}
        {contact.inbound_since_invite && (
          contact.inbound_since_invite.count > 0 ? (
            <span className="flex items-center gap-1 text-[11px] text-emerald-500" data-testid="supplier-reply-status">
              <CheckCircle2 className="h-3.5 w-3.5" /> {contact.inbound_since_invite.count} invoice{contact.inbound_since_invite.count === 1 ? "" : "s"} arrived via the forwarding address · last {new Date(contact.inbound_since_invite.last_at).toLocaleDateString("en-GB")}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-amber-500" data-testid="supplier-reply-status">
              <Clock className="h-3.5 w-3.5" /> Nothing received at the forwarding address yet
            </span>
          )
        )}
      </div>
    )}
    <SupplierInviteDialog open={invite} onOpenChange={setInvite} supplierKey={s.supplier_key} supplierName={s.supplier}
                          contactEmail={contact?.email} onSent={() => getSupplierContact(s.supplier_key).then(setContact)} />
    <StatementMatcher supplier={s.supplier} supplierKey={s.supplier_key} />
  </div>
  );
};

export default function Suppliers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null); // {s, code}
  const [busy, setBusy] = useState(false);
  const [inviteAll, setInviteAll] = useState(false);
  const { user } = useApp();
  const navigate = useNavigate();

  const load = () => getSuppliers().then(setRows);
  useEffect(() => {
    load();
  }, []);

  const confirmForget = async () => {
    setBusy(true);
    try {
      const res = await forgetSupplierMemory(pending.s.supplier_key, pending.code?.gl_code, user);
      toast.success(
        pending.code
          ? `Forgot GL ${pending.code.gl_code} for ${res.supplier}`
          : `Forgot all learned codes for ${res.supplier} (${res.deleted} cleared)`
      );
      setPending(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not forget learned codes");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => r.supplier.toLowerCase().includes(s)) : rows;
  }, [rows, q]);

  const totals = useMemo(
    () =>
      (rows || []).reduce(
        (a, r) => ({ spend: a.spend + r.total_spend, flagged: a.flagged + r.flagged_count, n: a.n + r.invoice_count }),
        { spend: 0, flagged: 0, n: 0 }
      ),
    [rows]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Suppliers</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Who you pay, what D20 has learned about them, and where the exceptions come from.
          </p>
        </div>
        {rows && (
          <div className="flex gap-6 text-sm" data-testid="suppliers-summary">
            <Stat label="Suppliers" value={rows.length} />
            <Stat label="Total spend" value={money(totals.spend)} />
            <Stat label="Flagged" value={`${totals.flagged}/${totals.n}`} className={flagTone(totals.n ? (totals.flagged / totals.n) * 100 : 0)} />
          </div>
        )}
      </div>

      <ChasesCard />

      <div className="relative max-w-md flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search suppliers…" className="pl-9" />
      </div>
      <div className="flex flex-wrap items-center gap-3 -mt-3">
        <Button variant="outline" size="sm" data-testid="invite-all-btn" onClick={() => setInviteAll(true)}>
          <Mail className="h-4 w-4 mr-1.5" /> Invite all suppliers to send invoices here
        </Button>
        <span className="text-xs text-muted-foreground">Emails every supplier with an address on file that hasn't been invited yet.</span>
      </div>
      <SupplierInviteDialog open={inviteAll} onOpenChange={setInviteAll} bulk onSent={load} />

      {!rows && (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {rows && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="suppliers-empty">
          No suppliers yet — upload an invoice to get started.
        </div>
      )}
      {rows && filtered.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="suppliers-grid">
          {filtered.map((s) => (
            <SupplierCard
              key={s.supplier_key}
              s={s}
              onOpen={(name) => navigate(`/review?supplier=${encodeURIComponent(name)}`)}
              onForget={(sup, code) => setPending({ s: sup, code })}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <AlertDialogContent data-testid="forget-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eraser className="h-4 w-4 text-orange-400" />
              {pending?.code ? `Forget GL ${pending.code.gl_code} for ${pending?.s.supplier}?` : `Forget everything learned about ${pending?.s.supplier}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.code
                ? `D20 will stop suggesting ${pending.code.gl_code} — ${pending.code.gl_name} for this supplier based on past approvals. Existing invoices are not changed.`
                : `All ${pending?.s.learned_codes.length || 0} learned GL code(s) for this supplier will be cleared, so future invoices are coded purely by the AI until you approve again. Existing invoices are not changed.`}
              {" "}This is written to the audit trail as {user}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button data-testid="confirm-forget-btn" onClick={(e) => { e.preventDefault(); confirmForget(); }} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eraser className="h-4 w-4 mr-2" />}
                Forget
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
